import { get, omit } from 'lodash';
import { PERSON_TYPE } from '../../../constants';

export function mapApiClientToStore(client) {
    if (!client || typeof client === 'string') {
        return { type: PERSON_TYPE.person, openModal: false, data: null };
    }
    if (get(client, 'organization')) {
        return {
            type: PERSON_TYPE.organization,
            openModal: false,
            data: { ...get(client, 'organization'), id: get(client, '_id') },
        };
    }
    if (get(client, 'person')) {
        return {
            type: PERSON_TYPE.person,
            openModal: false,
            data: { ...get(client, 'person'), id: get(client, '_id') },
        };
    }
    return { type: PERSON_TYPE.person, openModal: false, data: null };
}

export function mapApiPledgersToStore(pledgers = []) {
    return pledgers
        .filter((p) => p && typeof p === 'object')
        .map((p) => {
            const isOrg = !!get(p, 'organization');
            return {
                ...(isOrg ? get(p, 'organization') : get(p, 'person')),
                type: isOrg ? PERSON_TYPE.organization : PERSON_TYPE.person,
                id: get(p, '_id'),
            };
        });
}

export function buildPledgersSelect(pledgers = []) {
    return pledgers
        .filter((p) => p && typeof p === 'object')
        .map((p) => {
            const isOrg = !!get(p, 'organization');
            const label = isOrg
                ? get(p, 'organization.name', '')
                : `${get(p, 'person.fullName.lastname', '')} ${get(p, 'person.fullName.firstname', '')}`.trim();
            return { value: get(p, '_id'), label };
        });
}

export function mapApiObjectsToStore(objectOfInsurance = []) {
    return (objectOfInsurance || []).map((obj, index) => ({
        id: index,
        objectOfInsurance: obj,
    }));
}

export function resolveRiskId(source) {
    if (!source) {
        return null;
    }
    const risk = get(source, 'risk', source);
    if (risk && typeof risk === 'object') {
        return get(risk, '_id');
    }
    return risk;
}

export function normalizeRiskDetailsForEdit(riskDetails = [], productRisks = [], agreement) {
    if (riskDetails.length > 0) {
        return riskDetails.map((row) => {
            const riskId = resolveRiskId(row);
            return {
                ...row,
                risk: riskId,
                _id: riskId ?? get(row, '_id'),
                startDate: get(row, 'startDate', get(agreement, 'startOfInsurance')),
                endDate: get(row, 'endDate', get(agreement, 'endOfInsurance')),
            };
        });
    }
    return (productRisks || []).map((_r) => ({
        ..._r,
        startDate: get(agreement, 'startOfInsurance'),
        endDate: get(agreement, 'endOfInsurance'),
        insurancePremium: 0,
        insuranceRate: 0,
        insuranceSum: 0,
    }));
}

export function resolveRefId(value) {
    if (value == null || value === '') {
        return null;
    }
    if (typeof value === 'object') {
        return get(value, '_id');
    }
    return value;
}

export function normalizeFranchiseRow(row = {}) {
    return {
        ...row,
        risk: resolveRiskId(row),
        franchiseType: resolveRefId(get(row, 'franchiseType')),
        franchiseBase: resolveRefId(get(row, 'franchiseBase')),
        hasFranchise: !!get(row, 'hasFranchise'),
        isFixed: !!get(row, 'isFixed'),
        fixedValue: get(row, 'fixedValue', 0) ?? 0,
        franchise: get(row, 'franchise', '') ?? '',
    };
}

function findSavedFranchiseByRisk(saved, riskId) {
    if (riskId == null) {
        return undefined;
    }
    return saved.find((row) => String(resolveRiskId(row)) === String(riskId));
}

export function getFranchiseRows(agreement) {
    const productFranchise = get(agreement, 'product.franchise', []) || [];
    const saved = get(agreement, 'franchise', []) || [];

    if (saved.length === 0) {
        return productFranchise.map((row) => normalizeFranchiseRow(row));
    }

    if (productFranchise.length > 0) {
        return productFranchise.map((template) => {
            const riskId = resolveRiskId(template);
            const savedRow = findSavedFranchiseByRisk(saved, riskId) || {};
            return normalizeFranchiseRow({
                ...template,
                ...savedRow,
                risk: riskId ?? resolveRiskId(savedRow),
            });
        });
    }

    return saved.map((row) => normalizeFranchiseRow(row));
}

export function mapApiCommissionsToStore(commissions = []) {
    return (commissions || []).map((item, index) => ({
        ...item,
        id: get(item, 'id', index),
        agent: get(item, 'agent._id') || get(item, 'agent'),
    }));
}

export function toApiNumber(value) {
    if (value == null || value === '') {
        return undefined;
    }
    if (typeof value === 'object') {
        value = get(value, '_id', get(value, 'id', get(value, 'code')));
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
}

export function normalizeObjectOfInsuranceForSave(objects = []) {
    if (!Array.isArray(objects)) {
        return objects;
    }
    return objects.map((item) => {
        if (!item || typeof item !== 'object') {
            return item;
        }
        const region =
            toApiNumber(get(item, 'region')) ??
            toApiNumber(get(item, 'details.region'));
        const district =
            toApiNumber(get(item, 'district')) ??
            toApiNumber(get(item, 'details.district'));
        const country =
            toApiNumber(get(item, 'country')) ??
            toApiNumber(get(item, 'details.country'));

        const normalized = { ...item };
        if (region !== undefined) {
            normalized.region = region;
        }
        if (district !== undefined) {
            normalized.district = district;
        }
        if (country !== undefined) {
            normalized.country = country;
        }
        return normalized;
    });
}

export function getAgreementPatchUrl(agreementId) {
    return `api/agreement/${agreementId}`;
}

export function buildAgreementSaveAttributes(agreement, formData = {}, extras = {}) {
    const { commissions = [], insurer } = extras;
    const merged = { ...agreement, ...formData };
    const {
        product,
        insurant,
        beneficiary,
        pledgers,
        pledgersSelect,
        insurer: _insurer,
        branch,
        ...rest
    } = merged;

    const insurantId =
        (typeof insurant === 'object' ? get(insurant, '_id') : insurant) ||
        get(insurer, 'data.id');
    const beneficiaryId = typeof beneficiary === 'object' ? get(beneficiary, '_id') : beneficiary;
    const branchId = typeof branch === 'object' ? get(branch, '_id') : branch;

    const payload = {
        ...omit(rest, ['createdAt', 'updatedAt', '__v', 'status']),
        product: get(product, '_id') || product,
        insurant: insurantId,
        beneficiary: beneficiaryId,
        branch: branchId || branch,
        pledgers: Array.isArray(pledgers)
            ? pledgers.map((p) => (typeof p === 'object' ? get(p, '_id') || get(p, 'id') : p))
            : pledgers,
    };

    if (commissions.length > 0 && !get(payload, 'commission.length')) {
        payload.commission = commissions.map(({ id, ...row }) => row);
    }

    if (Array.isArray(payload.objectOfInsurance)) {
        payload.objectOfInsurance = normalizeObjectOfInsuranceForSave(payload.objectOfInsurance);
    }

    return payload;
}
